import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../../components/CustomButton';
import type { Booking } from './types';
import type { CancelSaveMode, CancellationFeeChoice } from '../../../api/booking/bookingWriteApiSlice';
import {
    BulkMessage,
    BulkOutcome,
    BulkResult,
    approveSkipReason,
    cancelSkipReason,
    runBulkAction,
} from '../../../utils/bookingBulkActions';
import {
    approveBookingStep,
    invalidateAfterBulkRun,
    makeCancelBookingStep,
} from '../../../flows/bookingBulkFlow';

type BulkAction = 'approve' | 'cancel';

interface Props {
    /** The selected bookings the list still shows (`selectedBookingsOf`). */
    selected: Booking[];
    /** `edit:bookings`: approve and cancel. Export is open to whoever sees the list. */
    canEdit: boolean;
    exportLoading: boolean;
    onExport: () => void;
    onClearSelection: () => void;
    /** After a run: refetch the list and clear the selection. */
    onFinished: () => void;
}

/** The modal frame the three dialogs share, in the cancel-fee dialog's look. */
const BulkDialog = ({ title, titleId, onClose, children, actions }: {
    title: string;
    titleId: string;
    onClose?: () => void;
    children: React.ReactNode;
    actions: React.ReactNode;
}) => (
    <div className={'fixed inset-0 z-[1000] flex items-center justify-center p-4'}>
        <div className={'fixed inset-0 bg-black bg-opacity-50 transition-opacity'} onClick={onClose} />
        <div
            className={'relative z-10 bg-white rounded-lg shadow-xl w-full max-w-lg mx-auto max-h-[90vh] flex flex-col'}
            role={'dialog'}
            aria-modal={'true'}
            aria-labelledby={titleId}
        >
            <div className={'p-6 flex flex-col gap-4 overflow-y-auto'}>
                <h3 className={'text-lg font-bold text-foreground'} id={titleId}>{title}</h3>
                {children}
            </div>
            <div className={'px-6 py-4 flex flex-row-reverse flex-wrap gap-3'}>{actions}</div>
        </div>
    </div>
);

const useMessageText = () => {
    const { t } = useTranslation();
    return (message?: BulkMessage) => {
        if (!message) return '';
        if (message.key) return t(message.key, message.params);
        return message.text ?? '';
    };
};

const OutcomeList = ({ heading, outcomes }: { heading: string, outcomes: BulkOutcome[] }) => {
    const messageText = useMessageText();
    if (outcomes.length === 0) return null;
    return (
        <div className={'flex flex-col gap-1'}>
            <p className={'text-sm font-semibold text-foreground'}>{heading}</p>
            <ul className={'flex flex-col gap-1'}>
                {outcomes.map(({ booking, message }) => (
                    <li key={booking.bookingId} className={'text-sm text-foreground'}>
                        <span className={'font-medium'}>{booking.bookingNumber}</span>
                        {message && <span className={'text-foreground/60'}>{`: ${messageText(message)}`}</span>}
                    </li>
                ))}
            </ul>
        </div>
    );
};

/**
 * Bulk actions on the bookings list (#3016): the bar over the table while rows
 * are selected, the confirm dialogs of approve and cancel, and the summary of
 * a run. Every run ends in the summary, which names each booking that failed
 * or was skipped and why; a partial failure is never shown as a success.
 */
const BookingsBulkActions = ({ selected, canEdit, exportLoading, onExport, onClearSelection, onFinished }: Props) => {
    const { t } = useTranslation();
    const [dialog, setDialog] = useState<BulkAction | null>(null);
    const [feeChoice, setFeeChoice] = useState<CancellationFeeChoice['choice'] | null>(null);
    const [progress, setProgress] = useState<{ done: number, total: number } | null>(null);
    const [pending, setPending] = useState<CancelSaveMode | 'approve' | null>(null);
    const [summary, setSummary] = useState<{ action: BulkAction, result: BulkResult } | null>(null);

    const running = progress !== null;
    const count = selected.length;
    const approvable = selected.filter((booking) => approveSkipReason(booking) === null).length;
    const cancelable = selected.filter((booking) => cancelSkipReason(booking) === null).length;

    const open = (action: BulkAction) => {
        setFeeChoice(null);
        setPending(null);
        setDialog(action);
    };
    const close = () => {
        if (!running) setDialog(null);
    };

    const run = async (action: BulkAction, saveMode?: CancelSaveMode) => {
        setPending(action === 'approve' ? 'approve' : saveMode ?? null);
        const onProgress = (done: number, total: number) => setProgress({ done, total });
        const result = action === 'approve'
            ? await runBulkAction(selected, approveSkipReason, approveBookingStep, onProgress)
            : await runBulkAction(
                selected,
                cancelSkipReason,
                makeCancelBookingStep(feeChoice ?? 'charge', saveMode ?? 'save_and_send_email'),
                onProgress,
            );
        invalidateAfterBulkRun();
        setProgress(null);
        setPending(null);
        setDialog(null);
        setSummary({ action, result });
        onFinished();
    };

    if (count === 0 && !summary) return null;

    const progressText = progress
        ? t('bookings.bulk.progress', { done: progress.done, total: progress.total })
        : null;

    return (
        <>
            {count > 0 && (
                <div
                    className={
                        'flex flex-col sm:flex-row sm:items-center gap-3 mb-3 px-4 py-3 '
                        + 'rounded-[5px] bg-text-contrast shadow-sm text-sm text-foreground'
                    }
                    role={'region'}
                    aria-label={t('bookings.bulk.bar_label')}
                >
                    <span className={'font-medium sm:mr-auto'}>{t('bookings.bulk.selected', { count })}</span>
                    <div className={'flex flex-wrap gap-2'}>
                        {canEdit && (
                            <>
                                <CustomButton
                                    title={t('bookings.bulk.approve')}
                                    isFilled={false}
                                    onClick={() => open('approve')}
                                />
                                <CustomButton
                                    title={t('bookings.bulk.cancel')}
                                    isFilled={false}
                                    onClick={() => open('cancel')}
                                />
                            </>
                        )}
                        <CustomButton
                            title={t('bookings.bookings_table.export_csv')}
                            isFilled={false}
                            loading={exportLoading}
                            onClick={onExport}
                        />
                        <CustomButton
                            title={t('bookings.bulk.clear')}
                            isFilled={false}
                            onClick={onClearSelection}
                        />
                    </div>
                </div>
            )}

            {dialog === 'approve' && (
                <BulkDialog
                    title={t('bookings.bulk.approve_dialog.title')}
                    titleId={'bulk-approve-title'}
                    onClose={close}
                    actions={(
                        <>
                            <CustomButton
                                title={t('bookings.bulk.approve_dialog.confirm')}
                                loading={running && pending === 'approve'}
                                disabled={running || approvable === 0}
                                onClick={() => run('approve')}
                            />
                            <div className={'mr-auto'}>
                                <CustomButton title={t('bookings.bulk.back')} isFilled={false} onClick={close} />
                            </div>
                        </>
                    )}
                >
                    <p className={'text-sm text-foreground'}>
                        {approvable > 0
                            ? t('bookings.bulk.approve_dialog.description', { count: approvable })
                            : t('bookings.bulk.approve_dialog.none')}
                    </p>
                    {approvable > 0 && count > approvable && (
                        <p className={'text-sm text-foreground/60'}>
                            {t('bookings.bulk.approve_dialog.skipped_note', { count: count - approvable })}
                        </p>
                    )}
                    {progressText && <p className={'text-sm text-foreground/60'} role={'status'}>{progressText}</p>}
                </BulkDialog>
            )}

            {dialog === 'cancel' && (
                <BulkDialog
                    title={t('bookings.bulk.cancel_dialog.title')}
                    titleId={'bulk-cancel-title'}
                    onClose={close}
                    actions={(
                        <>
                            <CustomButton
                                title={t('bookings.bulk.cancel_dialog.with_email')}
                                loading={running && pending === 'save_and_send_email'}
                                disabled={running || cancelable === 0 || feeChoice === null}
                                onClick={() => run('cancel', 'save_and_send_email')}
                            />
                            <CustomButton
                                title={t('bookings.bulk.cancel_dialog.without_email')}
                                isFilled={false}
                                loading={running && pending === 'save_only'}
                                disabled={running || cancelable === 0 || feeChoice === null}
                                onClick={() => run('cancel', 'save_only')}
                            />
                            <div className={'mr-auto'}>
                                <CustomButton title={t('bookings.bulk.cancel_dialog.keep')} isFilled={false} onClick={close} />
                            </div>
                        </>
                    )}
                >
                    <p className={'text-sm text-foreground'}>
                        {cancelable > 0
                            ? t('bookings.bulk.cancel_dialog.description', { count: cancelable })
                            : t('bookings.bulk.cancel_dialog.none')}
                    </p>
                    {cancelable > 0 && count > cancelable && (
                        <p className={'text-sm text-foreground/60'}>
                            {t('bookings.bulk.cancel_dialog.skipped_note', { count: count - cancelable })}
                        </p>
                    )}
                    {cancelable > 0 && (
                        <fieldset className={'flex flex-col gap-2'}>
                            <legend className={'text-sm font-semibold text-foreground mb-1'}>
                                {t('bookings.bulk.cancel_dialog.fee_label')}
                            </legend>
                            {/* Neither preselected, as in the single fee dialog: the
                                choice is the decision this dialog puts in front of staff. */}
                            {(['charge', 'waive'] as const).map((value) => (
                                <label className={'flex items-center gap-2'} key={value}>
                                    <input
                                        type={'radio'}
                                        name={'bulk-cancellation-fee-choice'}
                                        value={value}
                                        checked={feeChoice === value}
                                        onChange={() => setFeeChoice(value)}
                                        disabled={running}
                                        className={'accent-primary'}
                                    />
                                    <span className={'text-sm text-foreground'}>
                                        {t(`bookings.bulk.cancel_dialog.fee_${value}`)}
                                    </span>
                                </label>
                            ))}
                            <p className={'text-xs text-foreground/60'}>{t('bookings.bulk.cancel_dialog.fee_hint')}</p>
                        </fieldset>
                    )}
                    {progressText && <p className={'text-sm text-foreground/60'} role={'status'}>{progressText}</p>}
                </BulkDialog>
            )}

            {summary && (
                <BulkDialog
                    title={t(summary.action === 'approve'
                        ? 'bookings.bulk.summary.title_approve'
                        : 'bookings.bulk.summary.title_cancel')}
                    titleId={'bulk-summary-title'}
                    actions={(
                        <CustomButton title={t('bookings.bulk.summary.close')} onClick={() => setSummary(null)} />
                    )}
                >
                    <p className={'text-sm text-foreground'} role={'status'}>
                        {[
                            t('bookings.bulk.summary.succeeded', { count: summary.result.succeeded.length }),
                            t('bookings.bulk.summary.failed', { count: summary.result.failed.length }),
                            t('bookings.bulk.summary.skipped', { count: summary.result.skipped.length }),
                        ].join(' · ')}
                    </p>
                    <OutcomeList heading={t('bookings.bulk.summary.failed_heading')} outcomes={summary.result.failed} />
                    <OutcomeList
                        heading={t('bookings.bulk.summary.warning_heading')}
                        outcomes={summary.result.succeeded.filter((outcome) => outcome.message)}
                    />
                    <OutcomeList heading={t('bookings.bulk.summary.skipped_heading')} outcomes={summary.result.skipped} />
                </BulkDialog>
            )}
        </>
    );
};

export default BookingsBulkActions;
